// Ticket Purchase Types and Interfaces
// Request Functions
import { mutationRequest } from "../util/request"

export interface TicketPurchase {
  amount: number
  band_id: number
  band_share?: number
  bn_share?: number
  promotion_share?: number
  client_secret?: string
  created_at: string
  id: number
  last_four?: string
  user_default_payment_method_last_four?: string
  payment_method?: string | null
  post_id: number
  redeemed: boolean
  redeemed_at: string | null
  redeemed_by_name: string | null
  can_refund: boolean
  status: TicketPurchaseStatus
  ticket_id: string
  updated_at: string
  user_id: number | null
  guest_name?: string | null
  guest_email?: string | null
  event_performance_interest_ids?: number[]
}

export type TicketPurchaseStatus =
  | "cancelled"
  | "cancelled_and_refunded"
  | "cannot_refund"
  | "charged"
  | "charged_and_paid"
  | "comped"
  | "confirmed_by_user"
  | "failed"
  | "initialized"
  | "pending_charge"
  | "refund_failed"
  | "refunded_by_admin"
  | "refunded_by_band"

// Terminal cancel/refund outcomes - mirrors
// Api::V1::TicketPurchasesController::CANCELLED_TICKET_STATUSES. Shown in
// the manage-tickets list (not acted on) so an admin can confirm a
// cancellation actually went through.
const CANCELLED_TICKET_STATUSES: TicketPurchaseStatus[] = [
  "cancelled",
  "cancelled_and_refunded",
  "cannot_refund",
  "refund_failed",
  "refunded_by_admin",
  "refunded_by_band",
]

export const isCancelledTicketStatus = (
  status: TicketPurchaseStatus,
): boolean => CANCELLED_TICKET_STATUSES.includes(status)

export const cancelledTicketStatusLabel = (
  status: TicketPurchaseStatus,
): string => {
  switch (status) {
    case "cancelled":
      return "Cancelled"
    case "cancelled_and_refunded":
    case "refunded_by_admin":
    case "refunded_by_band":
      return "Refunded"
    case "cannot_refund":
      return "Cancelled (already paid out)"
    case "refund_failed":
      return "Refund Failed"
    default:
      return "Cancelled"
  }
}

export interface TicketPurchaseResponse {
  success: boolean
  // Create endpoint returns an array
  ticket_purchases?: TicketPurchase[]
  // my_ticket and show endpoints return a single ticket purchase
  ticket_purchase?: TicketPurchase
  errors?: {
    error: string
  }
}

export interface TicketPurchasesIndexResponse {
  success: boolean
  post?: {
    id: number
    title: string
    ticket_type: string
    ticket_price: number
    max_tickets: number
    nada_url: string
    tickets_sold: number
    tickets_remaining: number
  }
  tickets?: TicketPurchaseWithPurchaser[]
  total_count?: number
  redeemed_count?: number
  unredeemed_count?: number
  comped_count?: number
  referral_count?: number
  page?: number
  total_pages?: number
  per_page?: number
  has_next_page?: boolean
}

export interface TicketPurchaseWithPurchaser extends TicketPurchase {
  purchaser: {
    id: number
    name: string
    email: string
    first_name: string
    last_name: string
  } | null
}

export interface TicketPurchaseCreateParams {
  ticket_purchase: {
    post_id: number
    is_new_card?: boolean
    quantity?: number
    chosen_price?: number
  }
}

export interface TicketPurchaseUpdateParams {
  ticket_purchase: {
    payment_method?: string
    card_confirmed?: boolean
    status?: TicketPurchaseStatus
    quantity?: number
    save_card?: boolean
  }
}

export interface TicketPurchaseRedeemResponse {
  success: boolean
  message: string
}

export interface TicketPurchasesResponse {
  success: boolean
  ticket_purchases: TicketPurchase[]
  errors?: {
    error: string
  }
}

/**
 * Create a new ticket purchase
 */
export async function createTicketPurchase(
  bandId: number,
  params: TicketPurchaseCreateParams,
): Promise<TicketPurchaseResponse> {
  return mutationRequest({
    options: { method: "POST" },
    path: `/api/v1/bands/${bandId}/ticket_purchases`,
    data: params,
  })
}

/**
 * Update an existing ticket purchase
 */
export async function updateTicketPurchase(
  bandId: number,
  ticketPurchaseId: number,
  params: TicketPurchaseUpdateParams,
): Promise<TicketPurchaseResponse> {
  return mutationRequest({
    options: { method: "PUT" },
    path: `/api/v1/bands/${bandId}/ticket_purchases/${ticketPurchaseId}`,
    data: params,
  })
}

/**
 * Get ticket purchases for a post (band management view)
 */
export async function getTicketPurchasesForPost(
  postId: number,
  params?: {
    search?: string
    redeemed?: boolean
    page?: number
    per_page?: number
  },
): Promise<TicketPurchasesIndexResponse> {
  const queryParams: Record<string, string> = {}
  if (params?.search) queryParams.search = params.search
  if (params?.redeemed !== undefined)
    queryParams.redeemed = params.redeemed.toString()
  if (params?.page !== undefined) queryParams.page = params.page.toString()
  if (params?.per_page !== undefined)
    queryParams.per_page = params.per_page.toString()

  return mutationRequest({
    options: { method: "GET" },
    path: `/api/v1/posts/${postId}/ticket_purchases`,
    params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
  })
}

/**
 * Get current user's ticket purchases for a specific post
 */
export async function getMyTicketsForPost(
  postId: number,
): Promise<TicketPurchasesResponse> {
  return mutationRequest({
    options: { method: "GET" },
    path: `/api/v1/posts/${postId}/ticket_purchases/my_tickets`,
  })
}

/**
 * Get a specific ticket purchase by ID
 */
export async function getTicketPurchase(
  postId: number,
  ticketPurchaseId: number,
): Promise<TicketPurchaseResponse> {
  return mutationRequest({
    options: { method: "GET" },
    path: `/api/v1/posts/${postId}/ticket_purchases/${ticketPurchaseId}`,
  })
}

/**
 * Redeem a ticket purchase
 */
export async function redeemTicketPurchase(
  postId: number,
  ticketPurchaseId: number,
): Promise<TicketPurchaseRedeemResponse> {
  return mutationRequest({
    options: { method: "POST" },
    path: `/api/v1/posts/${postId}/ticket_purchases/${ticketPurchaseId}/redeem`,
  })
}

/**
 * Comp a guest ticket (no user account required)
 */
export async function compTicket(
  postId: number,
  guestName: string,
  guestEmail?: string,
): Promise<{ success: boolean; ticket_purchase?: TicketPurchase; error?: string }> {
  return mutationRequest({
    options: { method: "POST" },
    path: `/api/v1/posts/${postId}/ticket_purchases/comp`,
    data: { guest_name: guestName, guest_email: guestEmail || undefined },
  })
}

export interface GuestCheckoutParams {
  postId: number
  email: string
  paymentMethodId: string
  quantity?: number
  chosenPrice?: number | null
  redirectUrl?: string
  saveCard?: boolean
  referralToken?: string | null
}

export interface GuestCheckoutResponse {
  success: boolean
  user_token?: string
  ticket_purchases?: Array<{
    id: number
    ticket_id: string
    amount: number
    status: string
    guest_email: string
  }>
  error?: string
  error_code?: string
  stripe_error?: string
}

export async function updateGuestProfile(params: {
  token: string
  firstName: string
  lastName: string
  redirectUrl: string
}): Promise<{ success: boolean; errors?: string[] }> {
  return mutationRequest({
    options: { method: "PATCH" },
    path: `/api/v1/guest_profile`,
    data: {
      token: params.token,
      first_name: params.firstName,
      last_name: params.lastName,
      redirect_url: params.redirectUrl,
    },
  })
}

/**
 * Purchase a ticket as a guest (no account required)
 */
export async function guestCheckout(
  params: GuestCheckoutParams,
): Promise<GuestCheckoutResponse> {
  return mutationRequest({
    options: { method: "POST" },
    path: `/api/v1/guest_ticket_purchases`,
    data: {
      post_id: params.postId,
      email: params.email,
      payment_method_id: params.paymentMethodId,
      quantity: params.quantity,
      chosen_price: params.chosenPrice ?? undefined,
      redirect_url: params.redirectUrl,
      save_card: params.saveCard ?? true,
      referral_token: params.referralToken ?? localStorage.getItem("referral_token"),
    },
  })
}

/**
 * Unredeem a ticket purchase
 */
export async function unredeemTicketPurchase(
  postId: number,
  ticketPurchaseId: number,
): Promise<TicketPurchaseRedeemResponse> {
  return mutationRequest({
    options: { method: "POST" },
    path: `/api/v1/posts/${postId}/ticket_purchases/${ticketPurchaseId}/unredeem`,
  })
}

export interface BandInterestsResponse {
  success: boolean
  event_performance_ids?: number[]
  error?: string
}

/**
 * Records which band(s) on a multi-act bill the buyer bought this ticket to
 * see. Replaces any previous selection for this ticket purchase.
 *
 * guestToken is required for a guest checkout purchase, which has no JWT
 * session yet - it's that specific ticket's own ticket_id (from
 * guestCheckout()'s response), which only proves control of this one
 * purchase. Omit it for an authenticated purchase, where the normal JWT
 * applies.
 */
export async function saveTicketPurchaseBandInterests(
  postId: number,
  ticketPurchaseId: number,
  eventPerformanceIds: number[],
  guestToken?: string,
): Promise<BandInterestsResponse> {
  return mutationRequest({
    options: { method: "POST" },
    path: `/api/v1/posts/${postId}/ticket_purchases/${ticketPurchaseId}/band_interests`,
    data: {
      event_performance_ids: eventPerformanceIds,
      guest_token: guestToken || undefined,
    },
  })
}

/**
 * Delete a not-yet-redeemed guest list (comped) ticket
 */
export async function deleteTicketPurchase(
  postId: number,
  ticketPurchaseId: number,
): Promise<TicketPurchaseRedeemResponse> {
  return mutationRequest({
    options: { method: "DELETE" },
    path: `/api/v1/posts/${postId}/ticket_purchases/${ticketPurchaseId}`,
  })
}

// Utility functions for common operations
export const TicketPurchaseUtils = {
  /**
   * Check if a ticket purchase is in a completed state
   */
  isCompleted: (ticketPurchase: TicketPurchase): boolean => {
    return ["charged_and_paid", "confirmed_by_user", "charged"].includes(
      ticketPurchase.status,
    )
  },

  /**
   * Check if a ticket purchase can be redeemed
   */
  canBeRedeemed: (ticketPurchase: TicketPurchase): boolean => {
    return (
      TicketPurchaseUtils.isCompleted(ticketPurchase) &&
      !ticketPurchase.redeemed
    )
  },

  /**
   * Get display status for a ticket purchase
   */
  getDisplayStatus: (ticketPurchase: TicketPurchase): string => {
    if (ticketPurchase.redeemed) return "Redeemed"

    switch (ticketPurchase.status) {
      case "initialized":
        return "Pending Payment"
      case "confirmed_by_user":
        return "Confirmed"
      case "charged":
        return "Processing"
      case "charged_and_paid":
        return "Paid"
      case "failed":
        return "Failed"
      default:
        return "Unknown"
    }
  },

  /**
   * Format ticket ID for display
   */
  formatTicketId: (ticketId: string): string => {
    return ticketId || "N/A"
  },

  /**
   * Format amount for display
   */
  formatAmount: (amount: number): string => {
    return `$${amount.toFixed(2)}`
  },
}
